import { Fragment, FC } from 'react';
import { useDispatch } from 'react-redux';

import VSpacingContainer from 'bloko/blocks/vSpacing/VSpacingContainer';

import changeTariffParams from 'src/api/price/changeTariffParams';
import { useNotification } from 'src/components/Notifications/Provider';
import { DescriptorField, publicationsCountFields, Tariff, ZpProductFields } from 'src/models/price/priceZp';

import ActivationDate from 'src/pages/PriceZp/components/TariffFields/ActivationDate';
import TariffFieldCivCount from 'src/pages/PriceZp/components/TariffFields/TariffFieldCivCount';
import TariffFieldOptionsCount from 'src/pages/PriceZp/components/TariffFields/TariffFieldOptionsCount';
import TariffFieldPeriod from 'src/pages/PriceZp/components/TariffFields/TariffFieldPeriod';
import TariffFieldProfRoleGroup from 'src/pages/PriceZp/components/TariffFields/TariffFieldProfRoleGroup';
import TariffFieldPublicationsCount from 'src/pages/PriceZp/components/TariffFields/TariffFieldPublicationsCount';

const TariffControls: FC<{ tariff: Tariff }> = ({ tariff }) => {
    const { fields, fieldNames } = tariff;
    const dispatch = useDispatch();
    const { addNotification } = useNotification();

    const renderControl = (field: DescriptorField) => {
        const currentStringValue = tariff.params[field.name]?.toString();
        const stringChangeHandler = (value: string) =>
            dispatch(changeTariffParams(tariff, { [field.name]: value }, addNotification));
        const numberChangeHandler = (value: string) =>
            dispatch(changeTariffParams(tariff, { [field.name]: parseInt(value, 10) }, addNotification));
        if (field.name === ZpProductFields.ActivationDate) {
            return (
                <ActivationDate
                    value={currentStringValue}
                    minValue={field.minValue}
                    maxValue={field.maxValue}
                    onChange={stringChangeHandler}
                />
            );
        }
        if (!field.values) {
            return null;
        }
        if (field.name === ZpProductFields.Period) {
            return (
                <TariffFieldPeriod values={field.values} value={currentStringValue} onChange={numberChangeHandler} />
            );
        }
        if (field.name === ZpProductFields.CivCount) {
            return (
                <TariffFieldCivCount values={field.values} value={currentStringValue} onChange={numberChangeHandler} />
            );
        }
        if (field.name === ZpProductFields.ProfRoleGroup) {
            return (
                <TariffFieldProfRoleGroup
                    values={field.values}
                    value={currentStringValue}
                    onChange={stringChangeHandler}
                />
            );
        }
        if (publicationsCountFields.includes(field.name)) {
            return (
                <TariffFieldPublicationsCount
                    values={field.values}
                    value={currentStringValue}
                    onChange={numberChangeHandler}
                />
            );
        }
        if (field.name === ZpProductFields.OptionCount) {
            return (
                <TariffFieldOptionsCount
                    values={field.values}
                    value={currentStringValue}
                    onChange={numberChangeHandler}
                />
            );
        }
        return null;
    };
    const controls = fieldNames.map((fieldName) => (
        <Fragment key={fieldName}>{renderControl(fields[fieldName])}</Fragment>
    ));
    return <VSpacingContainer base={4}>{controls}</VSpacingContainer>;
};

export default TariffControls;
